import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Card, CardHeader, CardBody, MetricCard, DataTable, Chart, ChartLegend,
  DashboardSkeleton, PageError,
} from "../components/index.js";
import { useApi } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

/**
 * Social page — audience + engagement from the normalized social store
 * (Metricool sync today; partner-pushed data renders identically).
 * KPI cards with prior-period deltas, reach/interactions trend, network
 * breakdown (drives ?network=), and a posts table sortable by interactions.
 */

const KPIS = [
  { key: "followers",    label: "Followers",      note: "latest" },
  { key: "reach",        label: "Reach" },
  { key: "impressions",  label: "Impressions" },
  { key: "interactions", label: "Interactions" },
  { key: "engagement",   label: "Engagement Rate", suffix: "%" },
  { key: "posts",        label: "Posts" },
];

const SERIES = [
  { key: "reach",        color: "#ff6804", label: "Reach" },
  { key: "interactions", color: "#a3a3a3", label: "Interactions" },
];

function deltaSub(cur, prior) {
  if (!prior) return { sub: "no prior-period data", trend: null };
  const pct = ((cur - prior) / prior) * 100;
  const sign = pct > 0 ? "+" : "";
  return { sub: `${sign}${pct.toFixed(1)}% vs prior`, trend: pct > 0 ? "up" : pct < 0 ? "down" : null };
}

function postInteractions(m = {}) {
  // Metricool supplies `interactions` directly; other providers may send the parts.
  if (typeof m.interactions === "number") return m.interactions;
  return (m.likes || 0) + (m.comments || 0) + (m.shares || 0) + (m.saves || 0);
}

export function Social() {
  const [searchParams] = useSearchParams();
  const [network, setNetwork] = useState("");
  const [sortDesc, setSortDesc] = useState(true);

  const q = new URLSearchParams();
  for (const k of ["from", "to", "clientId"]) if (searchParams.get(k)) q.set(k, searchParams.get(k));
  if (network) q.set("network", network);
  const { data, loading, error, reload } = useApi(`/api/dashboard/social?${q.toString()}`);

  if (loading) return <DashboardSkeleton />;
  if (error) return <PageError error={error} onRetry={() => reload()} />;

  const totals = data?.totals || {};
  const prior = data?.priorTotals || {};
  const byNetwork = data?.byNetwork || [];
  const timeline = data?.timeline || [];
  const posts = [...(data?.posts || [])].sort((a, b) =>
    sortDesc ? postInteractions(b.metrics) - postInteractions(a.metrics) : postInteractions(a.metrics) - postInteractions(b.metrics)
  );
  const hasData = data?.period?.hasRealData;
  const maxReach = Math.max(1, ...byNetwork.map((n) => n.reach));

  return (
    <div className="space-y-5 max-w-[1100px]">
      {network && (
        <div className="flex items-center gap-2 text-[12px] text-text-secondary">
          <span>Showing <span className="capitalize font-medium text-text-primary">{network}</span> only</span>
          <button type="button" onClick={() => setNetwork("")} className="text-accent hover:underline">Clear</button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {KPIS.map((k) => {
          const { sub, trend } = deltaSub(totals[k.key] || 0, prior[k.key] || 0);
          return <MetricCard key={k.key} label={k.label} value={totals[k.key] || 0} suffix={k.suffix} sub={k.note ? `${k.note} · ${sub}` : sub} trend={trend} />;
        })}
      </div>

      {!hasData ? (
        <Card>
          <div className="px-5 py-10 text-center text-[13px] text-text-muted">
            Social metrics appear here once this client's Metricool brand is connected and synced.
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader
              title="Reach & Interactions"
              subtitle={`${data.range.from} – ${data.range.to}${network ? ` · ${network}` : ""}`}
              right={<ChartLegend items={SERIES.map((s) => ({ label: s.label, color: s.color }))} />}
            />
            <CardBody>
              <Chart type="area" data={timeline} xKey="date" series={SERIES} height={220} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="By Network"
              subtitle="Reach this period · click to filter"
              right={network && (
                <button type="button" onClick={() => setNetwork("")} className="text-[12px] text-accent hover:underline">Clear filter</button>
              )}
            />
            <CardBody className="space-y-2.5">
              {byNetwork.map((n) => (
                <button type="button" key={n.network} onClick={() => setNetwork(n.network === network ? "" : n.network)}
                  className={`w-full flex items-center gap-3 text-left ${network && network !== n.network ? "opacity-50" : ""}`}>
                  <span className="w-24 flex-shrink-0 text-[12px] text-text-secondary capitalize truncate">{n.network}</span>
                  <div className="flex-1 h-4 bg-app-bg rounded overflow-hidden">
                    <div className="h-full bg-accent rounded" style={{ width: `${(n.reach / maxReach) * 100}%` }} />
                  </div>
                  <span className="w-24 text-right text-[12px] text-text-muted" style={{ fontFeatureSettings: "'tnum'" }}>
                    {n.reach.toLocaleString()} · {n.posts} posts
                  </span>
                </button>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Recent Posts"
              subtitle={`${posts.length} most recent · sorted by interactions ${sortDesc ? "↓" : "↑"}`}
              right={
                <button type="button" onClick={() => setSortDesc((v) => !v)} className="text-[12px] text-text-secondary hover:text-text-primary">
                  Flip sort
                </button>
              }
            />
            <DataTable
              columns={[
                { key: "publishedAt", label: "Date", render: (r) => timeAgo(r.publishedAt) },
                { key: "network", label: "Network", render: (r) => <span className="capitalize">{r.network}{r.mediaType ? ` · ${r.mediaType}` : ""}</span> },
                { key: "content", label: "Content", render: (r) => <span className="text-text-secondary">{(r.content || "—").slice(0, 80)}{(r.content || "").length > 80 ? "…" : ""}</span> },
                { key: "impressions", label: "Impressions", align: "right", render: (r) => r.metrics?.impressions ?? r.metrics?.reach ?? "—" },
                { key: "interactions", label: "Interactions", align: "right", render: (r) => (typeof r.metrics?.interactions === "number" ? r.metrics.interactions : postInteractions(r.metrics) || "—") },
                { key: "engagement", label: "Engagement", align: "right", render: (r) => (typeof r.metrics?.engagement === "number" ? `${r.metrics.engagement}%` : "—") },
                {
                  key: "postUrl", label: "", align: "right",
                  render: (r) => r.postUrl
                    ? <a href={r.postUrl} target="_blank" rel="noreferrer" className="text-[12px] text-accent hover:underline">View</a>
                    : <span className="text-text-muted">—</span>,
                },
              ]}
              rows={posts}
              emptyLabel="No posts in this period"
            />
          </Card>
        </>
      )}
    </div>
  );
}
